import React, { useEffect, useMemo, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  IoAddOutline,
  IoBodyOutline,
  IoChevronDownOutline,
  IoCloudDownloadOutline,
  IoDocumentTextOutline,
  IoEyeOutline,
  IoFlaskOutline,
  IoPulseOutline,
} from "react-icons/io5";
import GetData, { fetchFileBlob } from "../../../services/getData";
import EditableModal from "../../../components/layout/editableModal/EditableModal";
import Toast from "../../../components/layout/toast/Toast";
import {
  getUsiTypeOptions,
  getUsiTypeParam,
  isUsiDiagnostics,
} from "./usiFormSchema";
import "../patients.css";
import "./patientInbody.css";
import "./PatientAnalyses.css";
import "./patientDocs.css";

const getGroupOptions = (t) => [
  { value: "laboratory", label: t("diagnostics.groups.laboratory") },
  { value: "instrumental", label: t("diagnostics.groups.instrumental") },
];

const getTypeOptions = (t) => [
  { value: "usi", label: t("diagnostics.types.usi") },
  { value: "inbody_vizbody", label: t("diagnostics.types.inbody_vizbody") },
  { value: "analysis", label: t("diagnostics.types.analysis") },
];

const getAddKindPresets = (t) => ({
  analysis: {
    title: t("diagnostics.addTitles.analysis"),
    group_diagnostics: "laboratory",
    type_diagnostics: "analysis",
  },
  usi: {
    title: t("diagnostics.addTitles.usi"),
    group_diagnostics: "instrumental",
    type_diagnostics: "usi",
  },
});

const getDiagnosticsModalFields = (t) => [
  { name: "name", label: t("diagnostics.fields.name"), type: "text", required: true },
  { name: "date", label: t("diagnostics.fields.date"), type: "date", required: true },
  {
    name: "group_diagnostics",
    label: t("diagnostics.fields.group"),
    type: "select",
    required: true,
    options: getGroupOptions(t),
  },
  {
    name: "type_diagnostics",
    label: t("diagnostics.fields.type"),
    type: "select",
    required: true,
    options: getTypeOptions(t),
  },
  { name: "files", label: t("diagnostics.fields.file"), type: "file", multiple: false, required: true },
];

const getUsiModalFields = (t) => [
  {
    name: "name",
    label: t("diagnostics.fields.usi"),
    type: "select",
    required: true,
    options: getUsiTypeOptions(t),
  },
];

const toIsoDate = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatDate = (value) => {
  if (!value) return "—";
  const raw = String(value).trim();
  const isoDate = raw.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(isoDate)) return raw;
  const [year, month, day] = isoDate.split("-");
  return `${day}.${month}.${year}`;
};

const getFileExtension = (fileName) => {
  const parts = String(fileName ?? "").split(".");
  return parts.length > 1 ? parts.pop().toLowerCase() : "";
};

const getPrimaryFile = (row) => {
  if (Array.isArray(row?.files) && row.files.length > 0) {
    return row.files[0];
  }

  const fileId = Number(
    Array.isArray(row?.file_ids) && row.file_ids.length > 0
      ? row.file_ids[0]
      : row?.id_files,
  );

  if (fileId > 0) {
    return {
      id: fileId,
      name: String(row?.file_name ?? "").trim(),
    };
  }

  return null;
};

const getDiagnosticsDisplayName = (row) => {
  const userName = String(row?.name ?? "").trim();
  const fileName = String(getPrimaryFile(row)?.name ?? "").trim();

  if (userName && fileName) {
    return `${userName} (${fileName})`;
  }
  if (userName) return userName;
  if (fileName) return fileName;
  return "—";
};

function DiagnosticsFilePreview({ file }) {
  const { t } = useTranslation("patient");
  const [blobUrl, setBlobUrl] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fileId = Number(file?.id);
    if (!fileId) {
      setLoading(false);
      setError(t("diagnostics.preview.noFile"));
      return undefined;
    }

    let cancelled = false;
    let objectUrl = "";

    (async () => {
      setLoading(true);
      setError("");
      try {
        const blob = await fetchFileBlob(fileId, true);
        objectUrl = URL.createObjectURL(blob);
        if (!cancelled) {
          setBlobUrl(objectUrl);
        }
      } catch (fetchError) {
        if (!cancelled) {
          setError(fetchError?.message || t("diagnostics.preview.loadFailed"));
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
    };
  }, [file?.id, t]);

  if (loading) {
    return <div className="analysis-view-file__state">{t("diagnostics.preview.loading")}</div>;
  }

  if (error) {
    return <div className="analysis-view-file__state analysis-view-file__state--error">{error}</div>;
  }

  const ext = getFileExtension(file?.name);

  if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) {
    return (
      <div className="analysis-view-file">
        <img src={blobUrl} alt={file?.name || t("diagnostics.preview.fileAlt")} className="analysis-view-file__image" />
      </div>
    );
  }

  if (ext === "pdf") {
    return (
      <div className="analysis-view-file">
        <iframe
          src={blobUrl}
          title={file?.name || t("diagnostics.preview.fileAlt")}
          className="analysis-view-file__frame"
        />
      </div>
    );
  }

  return (
    <div className="analysis-view-file">
      <div className="analysis-view-file__state">{t("diagnostics.preview.unsupported")}</div>
    </div>
  );
}

function PatientDiagnostics() {
  const { t } = useTranslation("patient");
  const navigate = useNavigate();
  const { patientId } = useParams();
  const id_patient = Number(patientId);

  const addKindPresets = useMemo(() => getAddKindPresets(t), [t]);
  const diagnosticsModalFields = useMemo(() => getDiagnosticsModalFields(t), [t]);
  const usiModalFields = useMemo(() => getUsiModalFields(t), [t]);

  const [diagnostics, setDiagnostics] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [addKind, setAddKind] = useState("analysis");
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [downloadingFile, setDownloadingFile] = useState(false);
  const [selectedDiagnostics, setSelectedDiagnostics] = useState(null);
  const [activeTab, setActiveTab] = useState("all");
  const [showAddMenu, setShowAddMenu] = useState(false);
  const addMenuRef = useRef(null);
  const [toast, setToast] = useState({});

  const addPreset = addKindPresets[addKind] || addKindPresets.analysis;
  const addModalFields = addKind === "usi" ? usiModalFields : diagnosticsModalFields;

  const initialData = useMemo(
    () => ({
      name: "",
      date: toIsoDate(new Date()),
      group_diagnostics: addPreset.group_diagnostics,
      type_diagnostics: addPreset.type_diagnostics,
      files: [],
    }),
    [addPreset],
  );

  const openAddModal = (kind) => {
    setAddKind(kind);
    setShowAddModal(true);
  };

  const showToast = (title, status = "success") => {
    setToast({
      id: Date.now(),
      title,
      status,
    });
  };

  const fetchDiagnostics = async () => {
    if (!id_patient) {
      setDiagnostics([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const data = await GetData({
        module: "patient",
        block: "diagnostics",
        id_patient,
      });

      if (Array.isArray(data?.result)) {
        setDiagnostics(data.result);
      } else if (data?.result) {
        setDiagnostics([data.result]);
      } else {
        setDiagnostics([]);
      }
    } catch (error) {
      console.error("Ошибка загрузки диагностик:", error);
      setDiagnostics([]);
      showToast(t("diagnostics.toast.loadFailed"), "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDiagnostics();
  }, [id_patient]);

  // Закрываем меню добавления кликом вне его
  useEffect(() => {
    if (!showAddMenu) return undefined;
    const handleClickOutside = (event) => {
      if (addMenuRef.current && !addMenuRef.current.contains(event.target)) {
        setShowAddMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showAddMenu]);

  const handleSaveDiagnostics = async (formData) => {
    const name = String(formData?.name ?? "").trim();
    const group_diagnostics = String(
      formData?.group_diagnostics ?? addPreset.group_diagnostics ?? "",
    ).trim();
    const type_diagnostics = String(
      formData?.type_diagnostics ?? addPreset.type_diagnostics ?? "",
    ).trim();
    const date = formData?.date || toIsoDate(new Date());
    const files = Array.isArray(formData?.files) ? formData.files : [];

    if (!name) {
      showToast(
        addKind === "usi"
          ? t("diagnostics.toast.selectUsi")
          : t("diagnostics.toast.enterName"),
        "error",
      );
      return false;
    }
    if (addKind === "usi") {
      try {
        const response = await GetData({
          module: "patient",
          block: "diagnostics",
          action: "add",
          id_patient,
          name,
          date,
          group_diagnostics: addPreset.group_diagnostics,
          type_diagnostics: "usi",
        });

        if (response?.status?.success === false) {
          showToast(
            response?.status?.message || t("diagnostics.toast.addUsiFailed"),
            "error",
          );
          return false;
        }

        const createdId = Number(response?.created_id);
        setShowAddModal(false);
        if (createdId > 0) {
          navigate(
            `/patient/${id_patient}/diagnostics/uzi/${createdId}?type=${encodeURIComponent(
              getUsiTypeParam(name),
            )}`,
          );
        } else {
          showToast(t("diagnostics.toast.usiAddedNoProtocol"), "error");
          fetchDiagnostics();
        }
        return true;
      } catch (error) {
        console.error("Ошибка добавления УЗИ:", error);
        showToast(t("diagnostics.toast.addUsiFailed"), "error");
        return false;
      }
    }
    if (!group_diagnostics) {
      showToast(t("diagnostics.toast.selectGroup"), "error");
      return false;
    }
    if (!type_diagnostics) {
      showToast(t("diagnostics.toast.selectType"), "error");
      return false;
    }
    if (!files.length) {
      showToast(t("diagnostics.toast.attachFile"), "error");
      return false;
    }

    try {
      const response = await GetData(
        {
          module: "patient",
          block: "diagnostics",
          action: "add",
          id_patient,
          name,
          date,
          group_diagnostics,
          type_diagnostics,
        },
        files,
      );

      if (response?.status?.success === false) {
        showToast(
          response?.status?.message || t("diagnostics.toast.addFailed"),
          "error",
        );
        return false;
      }

      showToast(response?.status?.message || t("diagnostics.toast.added"), "success");
      setShowAddModal(false);
      fetchDiagnostics();
      return true;
    } catch (error) {
      console.error("Ошибка добавления диагностики:", error);
      showToast(t("diagnostics.toast.addFailed"), "error");
      return false;
    }
  };

  const openDiagnosticsRow = (row) => {
    if (isUsiDiagnostics(row)) {
      if (!row?.id) return;
      const typeParam = getUsiTypeParam(row?.name);
      const params = new URLSearchParams();
      if (typeParam) params.set("type", typeParam);
      // Из списка протокол сначала открывается на просмотр,
      // редактирование включается кнопкой «Редактировать» внутри протокола.
      params.set("mode", "view");
      navigate(
        `/patient/${id_patient}/diagnostics/uzi/${row.id}?${params.toString()}`,
      );
      return;
    }
    handleViewDiagnostics(row);
  };

  const handleViewDiagnostics = async (row) => {
    if (!row?.id) return;

    setShowViewModal(true);
    setViewLoading(true);
    setSelectedDiagnostics(row);

    try {
      const data = await GetData({
        module: "patient",
        block: "diagnostics",
        action: "view",
        id_patient,
        id: row.id,
      });

      if (data?.status?.success === false) {
        showToast(data?.status?.message || t("diagnostics.toast.viewFailed"), "error");
        return;
      }

      if (data?.result) {
        setSelectedDiagnostics(data.result);
      }
    } catch (error) {
      console.error("Ошибка загрузки диагностики:", error);
      showToast(t("diagnostics.toast.viewFailed"), "error");
    } finally {
      setViewLoading(false);
    }
  };

  const handleDownloadFile = async (file) => {
    const fileId = Number(file?.id);
    if (!fileId || downloadingFile) return;

    setDownloadingFile(true);
    try {
      const blob = await fetchFileBlob(fileId, false);
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = file?.name || "file";
      link.click();
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      console.error("Ошибка скачивания файла:", error);
      showToast(error?.message || t("diagnostics.toast.downloadFailed"), "error");
    } finally {
      setDownloadingFile(false);
    }
  };

  const primaryFile = getPrimaryFile(selectedDiagnostics);

  // Вид записи: УЗИ / Инбади-Визбади / анализы / прочее
  const getRowKind = (row) => {
    if (isUsiDiagnostics(row)) return "usi";
    const type = String(row?.type_diagnostics ?? "").trim().toLowerCase();
    if (type === "inbody_vizbody" || type === "inbody" || type === "vizbody") {
      return "inbody_vizbody";
    }
    if (type === "analysis") return "analysis";
    return "other";
  };

  const getKindLabel = (kind) => {
    if (kind === "other") return t("diagnostics.list.other");
    return t(`diagnostics.types.${kind}`, { defaultValue: kind });
  };

  const renderKindIcon = (kind) => {
    if (kind === "usi") return <IoPulseOutline size={20} />;
    if (kind === "inbody_vizbody") return <IoBodyOutline size={20} />;
    if (kind === "analysis") return <IoFlaskOutline size={20} />;
    return <IoDocumentTextOutline size={20} />;
  };

  const getFileIconClass = (fileName) => {
    const ext = getFileExtension(fileName);
    if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext)) {
      return "pd-doc__ic--img";
    }
    if (ext === "pdf") return "pd-doc__ic--pdf";
    return "";
  };

  const listTabs = [
    { key: "all", label: t("diagnostics.list.tabs.all") },
    { key: "analysis", label: t("diagnostics.list.tabs.analysis") },
    { key: "usi", label: t("diagnostics.list.tabs.usi") },
    { key: "inbody_vizbody", label: t("diagnostics.list.tabs.inbody") },
  ];

  const filteredDiagnostics = diagnostics.filter(
    (row) => activeTab === "all" || getRowKind(row) === activeTab,
  );

  return (
    <div className="patient-dynamics patient-analyses pd">
      {toast?.title ? (
        <Toast
          key={toast.id}
          title={toast.title}
          status={toast.status}
          delay={3500}
        />
      ) : null}

      {/* Табы слева, раскрывающаяся кнопка добавления справа — одной строкой */}
      <div className="pd-toolbar">
        <div className="pd-tags">
          {listTabs.map((tabItem) => (
            <button
              key={tabItem.key}
              type="button"
              className={`pd-tag${activeTab === tabItem.key ? " is-active" : ""}`}
              onClick={() => setActiveTab(tabItem.key)}
              aria-pressed={activeTab === tabItem.key}
            >
              {tabItem.label}
            </button>
          ))}
        </div>

        <div className="pd-add" ref={addMenuRef}>
          <button
            type="button"
            className="pd-add__btn"
            onClick={() => setShowAddMenu((prev) => !prev)}
            aria-expanded={showAddMenu}
          >
            <IoAddOutline size={17} aria-hidden="true" />
            {t("diagnostics.buttons.add")}
            <IoChevronDownOutline
              size={14}
              aria-hidden="true"
              className={`pd-add__caret${showAddMenu ? " is-open" : ""}`}
            />
          </button>
          {showAddMenu ? (
            <div className="pd-add__menu" role="menu">
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setShowAddMenu(false);
                  openAddModal("analysis");
                }}
              >
                <IoFlaskOutline size={17} aria-hidden="true" />
                {t("diagnostics.buttons.addAnalysis")}
              </button>
              <button
                type="button"
                role="menuitem"
                onClick={() => {
                  setShowAddMenu(false);
                  openAddModal("usi");
                }}
              >
                <IoPulseOutline size={17} aria-hidden="true" />
                {t("diagnostics.buttons.addUsi")}
              </button>
            </div>
          ) : null}
        </div>
      </div>

      {loading ? (
        <div className="pd-empty">{t("state.loading", { ns: "common" })}</div>
      ) : filteredDiagnostics.length ? (
        <div className="pd-list">
          {filteredDiagnostics.map((row) => {
            const rowFile = getPrimaryFile(row);
            const kind = getRowKind(row);
            const displayName = getDiagnosticsDisplayName(row);
            const canView = Boolean(row?.id) || isUsiDiagnostics(row);
            const canDownload = Boolean(Number(rowFile?.id));

            return (
              <div
                className="pd-doc"
                key={row?.id ?? `${row?.created_date}-${row?.name}`}
              >
                <div
                  className={`pd-doc__ic ${getFileIconClass(rowFile?.name)}`}
                  aria-hidden="true"
                >
                  {renderKindIcon(kind)}
                </div>
                <div className="pd-doc__body">
                  <button
                    type="button"
                    className="pd-doc__name"
                    onClick={() => openDiagnosticsRow(row)}
                    disabled={!canView}
                    title={canView ? t("diagnostics.list.open") : displayName}
                  >
                    {displayName}
                  </button>
                  <div className="pd-doc__meta">
                    {formatDate(row?.created_date || row?.date)}
                  </div>
                </div>
                <span className="pd-badge">{getKindLabel(kind)}</span>
                <button
                  type="button"
                  className="pd-icon-btn"
                  onClick={() => handleDownloadFile(rowFile)}
                  disabled={!canDownload || downloadingFile}
                  title={t("diagnostics.list.download")}
                  aria-label={t("diagnostics.list.download")}
                >
                  <IoCloudDownloadOutline size={18} />
                </button>
                {canView ? (
                  <button
                    type="button"
                    className="pd-icon-btn"
                    onClick={() => openDiagnosticsRow(row)}
                    title={t("diagnostics.list.open")}
                    aria-label={t("diagnostics.list.open")}
                  >
                    <IoEyeOutline size={18} />
                  </button>
                ) : null}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="pd-empty">
          {diagnostics.length
            ? t("diagnostics.list.emptyFound")
            : t("diagnostics.table.empty")}
        </div>
      )}

      <EditableModal
        show={showAddModal}
        onHide={() => setShowAddModal(false)}
        title={addPreset.title}
        initialData={initialData}
        fields={addModalFields}
        onSave={handleSaveDiagnostics}
        closeOnSave={false}
      />

      <Modal
        show={showViewModal}
        onHide={() => {
          setShowViewModal(false);
          setSelectedDiagnostics(null);
        }}
        size="xl"
        centered
        className="analysis-view-modal analysis-view-modal--file"
      >
        <Modal.Header closeButton>
          <Modal.Title>{selectedDiagnostics?.name || t("diagnostics.view.fallbackTitle")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {viewLoading && !selectedDiagnostics ? (
            <div className="analysis-view-empty">{t("diagnostics.view.loading")}</div>
          ) : selectedDiagnostics ? (
            <div className="avf">
              <div className="avf__meta">
                <span className="avf__chip">
                  {t("diagnostics.view.date")}: {formatDate(selectedDiagnostics?.date)}
                </span>
                <span className="avf__chip">
                  {t("diagnostics.view.group")}:{" "}
                  {selectedDiagnostics?.group_diagnostics
                    ? t(`diagnostics.groups.${selectedDiagnostics.group_diagnostics}`, {
                        defaultValue: selectedDiagnostics.group_diagnostics,
                      })
                    : "—"}
                </span>
                <span className="avf__chip">
                  {t("diagnostics.view.type")}:{" "}
                  {selectedDiagnostics?.type_diagnostics
                    ? t(`diagnostics.types.${selectedDiagnostics.type_diagnostics}`, {
                        defaultValue: selectedDiagnostics.type_diagnostics,
                      })
                    : "—"}
                </span>
                {primaryFile?.name ? (
                  <span className="avf__chip avf__chip--file">{primaryFile.name}</span>
                ) : null}
              </div>

              <div className="avf__file">
                {primaryFile ? (
                  <DiagnosticsFilePreview file={primaryFile} />
                ) : (
                  <div className="analysis-view-empty">{t("diagnostics.preview.noFile")}</div>
                )}
              </div>
            </div>
          ) : (
            <div className="analysis-view-empty">{t("diagnostics.view.notFound")}</div>
          )}
        </Modal.Body>
        {selectedDiagnostics ? (
          <Modal.Footer className="analysis-view-modal__footer">
            <button
              type="button"
              className="button-default light"
              onClick={() => handleDownloadFile(primaryFile)}
              disabled={!primaryFile || downloadingFile}
            >
              {downloadingFile ? t("diagnostics.view.downloading") : t("diagnostics.view.download")}
            </button>
          </Modal.Footer>
        ) : null}
      </Modal>
    </div>
  );
}

export default PatientDiagnostics;
